import { useCallback, useMemo } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { agentWizardActiveJobs } from 'api/resources/agentWizard';
import { useIsPlgProd } from './useIsPlgProd';
import useProjectRoute from './useProjectRoute';
import useSampleProject from './useSampleProjects/useSampleProject';

// Queries active jobs endpoint, with utils for optimistic updates
const useAgentWizardActiveJob = () => {
  const { accountId } = useProjectRoute();
  const { inSampleProject } = useSampleProject();
  const isPlgProd = useIsPlgProd();
  const queryClient = useQueryClient();

  const queryKey = useMemo(
    () => [accountId, 'agentWizardActiveJobs'],
    [accountId],
  );

  const { data, isLoading, fetchStatus } = useQuery({
    queryKey,
    queryFn: () => agentWizardActiveJobs({ accountId }),
    enabled: !inSampleProject && !!accountId && isPlgProd,
  });

  const activeJob = useMemo(() => data?.jobs?.[0], [data]);

  // Currently context allows only one job at a time so this can be overly simple
  const setActiveJob = useCallback(
    (projectId?: string) => {
      queryClient.setQueryData(queryKey, (oldData: typeof data) => ({
        ...oldData,
        jobs: projectId ? [{ projectId }] : [],
      }));
    },
    [queryClient, queryKey],
  );

  return {
    activeJob,
    isLoadingActiveJob: isLoading && fetchStatus !== 'idle', //So it won't interfere outside plg
    setActiveJob,
  };
};

export default useAgentWizardActiveJob;
